import { Component, Input, OnInit, ViewChildren, QueryList, Output, EventEmitter, SimpleChanges } from '@angular/core'
import some from 'lodash-es/some'
import get from 'lodash-es/get'
import filter from 'lodash-es/filter'
import { BaseComponent } from 'components/base.component'
import { postInContext } from 'logic/post-states'
import { PostBox } from '../../../post-box/post-box.component'
import { Customer, Post, Submission, User } from '../../submission.queries'
import { submissionInContext } from 'src/logic/submission-states'
import { VideoBoxActionEvent, VideoBoxPurchaseEvent, VideoBoxCommentEvent, VideoBoxComponent } from 'src/modules/video-box/video-box.component'
import { isVideoPlanCode } from 'src/utils'
import { EditspaceService } from 'src/services/editspace.service'
@Component({
  selector: 'queued-submissions-list',
  styleUrls: ['queued-submissions-list.component.scss'],
  templateUrl: 'queued-submissions-list.component.html'
})
export class QueuedSubmissionsListComponent extends BaseComponent implements OnInit {
  @Input() customer: Customer
  @Input() currentUser: User
  @Input() posts: Post[] = []
  @Input() isSearch: boolean
  @Input() submissions: Submission[] = []
  @ViewChildren(PostBox) postboxes: QueryList<PostBox>
  @ViewChildren(VideoBoxComponent) videoBoxes: QueryList<VideoBoxComponent>
  @Output() deprecatedStoreChange: EventEmitter<number[]> = new EventEmitter<number[]>()
  @Output() action: EventEmitter<VideoBoxActionEvent> = new EventEmitter<VideoBoxActionEvent>()
  @Output() accept: EventEmitter<VideoBoxPurchaseEvent> = new EventEmitter<VideoBoxPurchaseEvent>()
  @Output() requestEdits: EventEmitter<VideoBoxCommentEvent> = new EventEmitter<VideoBoxCommentEvent>()
  uiState: any = {}
  queuedPosts: Post[] = []
  awaitingEditingPosts: Post[] = []
  queuedSubmissions: Submission[] = []
  awaitingEditingSubmissions: Submission[] = []
  awaitingAgencyReviewPosts: Post[] = []
  refetchSubmissionsQuery: EventEmitter<void> = new EventEmitter<void>()
  showPendingRequestsWarning = false
  showClientFeedbackWarning = false
  isEditspace: boolean = false

  constructor( private editspaceService: EditspaceService) {
    super()
  }

  ngOnInit(): void {
    this.isEditspace = this.editspaceService.isEditspace()
    this.observeUiState(uiState => this.uiState = uiState)
    this.handleChanges(this.customer)
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.customer) {
      const customer: Customer = changes.customer.currentValue
      this.handleChanges(customer)
    }
  }

  handleChanges(customer: Customer): void {
    this.queuedPosts = this._filteredAndOrdered(this.posts, { type: 'Post', context: 'queued', order: ['submissionPosition', 'id'] }) as Post[]
    this.awaitingEditingPosts = this._filteredAndOrdered(this.posts, { type: 'Post', context: 'awaiting_editing', order: 'updatedAt' }) as Post[]
    this.queuedSubmissions = this._filteredAndOrdered(this.submissions, { type: 'Submission', context: 'purchaseable', order: 'sentToCustomerAt' }) as Submission[]
    this.showPendingRequestsWarning = this.queuedPosts.some(p => p.lockStatus === 'me')
    this.awaitingEditingSubmissions = this._filteredAndOrdered(this.submissions, { type: 'Submission', context: 'awaiting_editing', order: 'updatedAt' }) as Submission[]
    this.awaitingAgencyReviewPosts = this._filteredAndOrdered(this.posts, { type: 'Post', context: 'awaiting_agency_review', order: 'updatedAt' }) as Post[]
    this.showClientFeedbackWarning = !this.isEditspace && this.awaitingAgencyReviewPosts.some(p => p.wlUserFeedbackMessage.includes('Your client requested edits'))
  }

  get showCreditInfo(): boolean {
    return !this.videoCustomer && this.currentUser && this.currentUser.canUpdateSubscription
  }

  get isCustomer(): boolean {
    return this.currentUser?.role === 'customer'
  }

  get isWhiteLabelAgency(): boolean {
    return this.currentUser?.canViewWhiteLabel
  }

  get isWhiteLabel(): boolean {
    return this.currentUser?.role === 'white_label'
  }

  get pendingRequestsWarningCopy(): string {
    return this.isEditspace ? 'and submit your feedback' : 'to send edits to the writer'
  }

  trackById(index: number, item: Post | Submission): number {
    return item.id
  }

  _filteredAndOrdered(submissions: Post[] | Submission[], { type, context, order }: { type: 'Post' | 'Submission', context: string, order: string | string[] }): Post[] | Submission[] {
    const filtered = filter(submissions, s => type === 'Post' ? postInContext(s as Post, context, this.isEditspace) : submissionInContext(s as Submission, context) )
    return filtered as Post[] | Submission[]
  }

  onRefetchSubmissionsQuery(): void {
    this.refetchSubmissionsQuery.emit()
  }

  get noRequests(): boolean {
    const activeKeywords = this.customer?.keywords?.entries.filter(r => r.state === 'active')
    const activeRequests = this.customer?.requests?.entries.filter(r => r.state === 'active')
    return activeKeywords?.length === 0 && activeRequests?.length === 0 && this.customer.assignedKeywordsCount === 0
  }

  get noPurchaseableSubmissions(): boolean {
    return !some(this.queuedPosts, (p) => postInContext(p, 'purchaseable')) && !some(this.queuedSubmissions, s => submissionInContext(s, 'purchaseable'))
      && !some(this.awaitingAgencyReviewPosts, (p) => postInContext(p, 'purchaseable'))
  }

  get videoCustomer(): boolean {
    return isVideoPlanCode(get(this.customer, 'subscription.plan.code'))
  }

  get showAwaitingEditingSubmissions(): boolean {
    return !this.isEditspace &&
            this.customer &&
            this.customer.state !== 'expired' &&
            (this.awaitingEditingPosts.length > 0 || this.awaitingEditingSubmissions.length > 0)
  }

}
